import { useEffect, useRef } from "react";
import type { SaveSummarySectionOptions } from "@/contexts/ValueAssessmentContext";

type PersistFn = (options?: SaveSummarySectionOptions) => void | Promise<void>;

export function useUnsavedChangesWarning(hasChanges: boolean, persist?: PersistFn) {
  const hasChangesRef = useRef(hasChanges);
  const persistRef = useRef(persist);

  useEffect(() => {
    hasChangesRef.current = hasChanges;
    persistRef.current = persist;
  }, [hasChanges, persist]);

  useEffect(() => {
    if (!persist) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasChangesRef.current || !persistRef.current) return;

      try {
        const result = persistRef.current({ keepAlive: true });
        if (result instanceof Promise) {
          // fire-and-forget
        }
      } catch {
        // ignore errors during unload
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [persist]);

  useEffect(() => {
    return () => {
      if (hasChangesRef.current && persistRef.current) {
        try {
          persistRef.current({ keepAlive: true });
        } catch {
          // ignore
        }
      }
    };
  }, []);
}
